import React, { useState } from "react"
import DateTimeField from "components/Common/DateTimeField"
import { Alert, Button, Col, FormFeedback, FormGroup, FormText, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap"
import { placeName, quantityLabel } from "pages/EventsManagement/components/Tabs/Logistics/labels"
import { DESTINATIONS, FIELD_LIMIT, TEXT_LIMIT, balanceLabel, balanceOf, entriesFor, entryErrors, entryKey, entryOf, itemsOf, placedName,
  placesOf, productName, reversedName, timeLabel, totalOf } from "./stockForm"

// A field's unit: a `box` is a can of balls.
const unitNames = { box: "Cans", pcs: "Pieces", set: "Sets", bottle: "Bottles", ball: "Balls" }
const unitName = unit => unitNames[unit] || unit
const groupingName = grouping => `${grouping.name[0].toUpperCase()}${grouping.name.slice(1)}s`

// A modal of the Stock page: closed only when nothing is pending; `failure` is Events' refusal.
const StockModal = ({ title, id, size, pending, failure, onClose, actions, children }) => <Modal isOpen centered size={size}
  toggle={pending ? undefined : onClose} labelledBy={id}>
  <ModalHeader id={id} toggle={pending ? undefined : onClose}>{title}</ModalHeader>
  <ModalBody>
    {children}
    {failure && <Alert color="danger" className="mt-3 mb-0">{failure}</Alert>}
  </ModalBody>
  <ModalFooter>
    <Button color="light" disabled={pending} onClick={onClose}>Cancel</Button>
    {actions}
  </ModalFooter>
</Modal>

// The fields of one place: the grouping packages and the units for a product that comes in them (cases
// and cans), otherwise the units alone, each labelled by its place. `prefix`, such as the product's name,
// completes the label assistive technology reads; in a table whose row names the product and place
// (`compact`), a single field's label is read, not shown.
const EntryFields = ({ product, place, entries, error, idPrefix, prefix, hint, compact = false, onChange }) => {
  const entry = entryOf(entries, place)
  const change = field => event => onChange({ ...entries, [entryKey(place)]: { ...entry, [field]: event.target.value } })
  const shown = part => [place && placeName(place), part].filter(Boolean).join(" · ")
  const field = (key, part) => <>
    <Label className={compact && !product.grouping ? "sr-only" : "small text-muted mb-1"} for={`${idPrefix}-${key}`}>{shown(part)}</Label>
    <Input id={`${idPrefix}-${key}`} aria-label={[prefix, shown(part)].filter(Boolean).join(" · ")} type="number" min={0} step={1} inputMode="numeric"
      value={entry[key]} invalid={Boolean(error)} onChange={change(key)} />
  </>
  const total = totalOf(product, entry)
  return <FormGroup className={compact ? "mb-0" : undefined}>
    {product.grouping ? <Row form>
      <Col xs={6}>{field("groupings", groupingName(product.grouping))}</Col>
      <Col xs={6}>{field("quantity", unitName(product.unit))}</Col>
    </Row> : field("quantity", place ? null : unitName(product.unit))}
    {error && <FormFeedback className="d-block">{error}</FormFeedback>}
    {product.grouping && total != null && <FormText>= {quantityLabel(total, product.unit)}</FormText>}
    {hint && <FormText>{hint}</FormText>}
  </FormGroup>
}

const KINDS = {
  recordReceipt: { title: "Receipt", none: "Enter what was received.", positive: true },
  recordManualIssue: { title: "Manual issue", none: "Enter what leaves the warehouse.", positive: true },
  recordInventory: { title: "Inventory count", none: "Enter the count of at least one place." },
}
// The commands MovementDialog records.
export const MOVEMENT_KINDS = Object.keys(KINDS)

// A receipt (with its supplier, document and day), a manual issue (with its destination and reason) or an
// inventory count (each place counted, with a reason when it differs from the balance), for one product.
export function MovementDialog({ kind, products, today, pending, failure, onSubmit, onClose }) {
  const [productId, setProductId] = useState(products[0]?.id || "")
  const product = products.find(({ id }) => id === productId) || products[0]
  const [entries, setEntries] = useState(() => entriesFor(product))
  const [form, setForm] = useState({ supplier: "", document: "", date: today, destination: DESTINATIONS[0][0], reason: "" })
  const [errors, setErrors] = useState({})
  const { title, none, positive } = KINDS[kind]
  const id = `logistics-stock-${kind}`
  const set = field => event => setForm({ ...form, [field]: event.target.value })
  const counting = kind === "recordInventory"
  const differences = counting ? placesOf(product).map(place => {
    const total = totalOf(product, entryOf(entries, place))
    return total == null ? null : total - balanceOf(product, place)
  }) : []

  const save = () => {
    const found = { ...entryErrors(product, entries, { positive }) }
    if (found.none) found.none = none
    if (kind === "recordReceipt") {
      if (!form.supplier.trim()) found.supplier = "Enter the supplier."
      if (!form.document.trim()) found.document = "Enter the document number."
      if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) found.date = "Enter the day the goods arrived."
      else if (form.date > today) found.date = "The goods cannot arrive after today."
    }
    if (kind === "recordManualIssue" && !form.reason.trim()) found.reason = "A reason is required."
    if (counting && !form.reason.trim() && differences.some(difference => difference)) found.reason = "A reason is required when the count differs from the balance."
    setErrors(found)
    if (Object.keys(found).length) return
    const body = { productId: product.id, items: itemsOf(product, entries), reason: form.reason.trim() }
    if (kind === "recordReceipt") Object.assign(body, { supplier: form.supplier.trim(), document: form.document.trim(), date: form.date })
    if (kind === "recordManualIssue") body.destination = form.destination
    onSubmit(kind, body)
  }
  const text = (field, label, props = {}) => <FormGroup>
    <Label for={`${id}-${field}`}>{label}</Label>
    {props.type === "date" ? <DateTimeField id={`${id}-${field}`} mode="date" value={form[field]} invalid={Boolean(errors[field])} onChange={set(field)} {...props} />
      : <Input id={`${id}-${field}`} value={form[field]} invalid={Boolean(errors[field])} onChange={set(field)} {...props} />}
    {errors[field] && <FormFeedback>{errors[field]}</FormFeedback>}
  </FormGroup>

  return <StockModal title={title} id={id} pending={pending} failure={failure} onClose={onClose}
    actions={<Button color="primary" disabled={pending} onClick={save}>{pending ? "Saving…" : "Save movement"}</Button>}>
    <FormGroup>
      <Label for={`${id}-product`}>Product</Label>
      <Input id={`${id}-product`} type="select" value={product.id} onChange={event => {
        const next = products.find(({ id: candidate }) => candidate === event.target.value)
        setProductId(next.id)
        setEntries(entriesFor(next))
        setErrors({})
      }}>
        {products.map(candidate => <option key={candidate.id} value={candidate.id}>{productName(candidate)}</option>)}
      </Input>
      <FormText>{balanceLabel(product)}</FormText>
    </FormGroup>
    <Row form>{placesOf(product).map((place, index) => <Col key={entryKey(place)} sm={product.places ? 6 : 12}>
      <EntryFields product={product} place={place} entries={entries} error={errors[entryKey(place)]} idPrefix={`${id}-${entryKey(place)}`}
        onChange={setEntries}
        hint={counting && differences[index] != null ? `Difference: ${differences[index] < 0 ? "−" : ""}${Math.abs(differences[index])}` : null} />
    </Col>)}</Row>
    {errors.none && <Alert color="warning" className="py-2">{errors.none}</Alert>}
    {kind === "recordReceipt" && <>
      <Row form>
        <Col sm={6}>{text("supplier", "Supplier", { maxLength: FIELD_LIMIT, placeholder: "e.g. Wilson distributor" })}</Col>
        <Col sm={6}>{text("document", "Document number", { maxLength: FIELD_LIMIT, placeholder: "e.g. NIR 1046" })}</Col>
      </Row>
      {text("date", "Day received", { type: "date", max: today })}
      {text("reason", "Notes", { maxLength: TEXT_LIMIT, placeholder: "optional" })}
    </>}
    {kind === "recordManualIssue" && <>
      {text("destination", "Destination", { type: "select", children: DESTINATIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>) })}
      {text("reason", "Reason", { maxLength: TEXT_LIMIT })}
    </>}
    {counting && <>
      <FormText className="d-block mb-2">The system records the difference from the balance as an adjustment.</FormText>
      {text("reason", "Reason", { maxLength: TEXT_LIMIT, placeholder: "required if the count differs" })}
    </>}
  </StockModal>
}

// The launch's physical count (US 121): every product kept in stock, each place apart, 0 included. It is
// recorded once, as the stock's starting point.
export function InitialInventoryDialog({ products, pending, failure, onSubmit, onClose }) {
  const [entries, setEntries] = useState(() => Object.fromEntries(products.map(product => [product.id, entriesFor(product)])))
  const [reason, setReason] = useState("")
  const [errors, setErrors] = useState({})
  const save = () => {
    const found = Object.fromEntries(products.map(product => [product.id, entryErrors(product, entries[product.id], { every: true })])
      .filter(([, productErrors]) => Object.keys(productErrors).length))
    setErrors(found)
    if (Object.keys(found).length) return
    onSubmit("recordInitialInventory", { items: products.flatMap(product => itemsOf(product, entries[product.id], true)), reason: reason.trim() })
  }
  return <StockModal title="Initial inventory" id="logistics-stock-initial" size="lg" pending={pending} failure={failure} onClose={onClose}
    actions={<Button color="primary" disabled={pending} onClick={save}>{pending ? "Saving…" : "Record initial inventory"}</Button>}>
    <p className="text-muted">Count every product kept in stock at Câmpina, each place apart, 0 included. The counts become the stock's starting
      point: the shipments validated before it change no stock, and it is recorded once.</p>
    <Table size="sm" className="logistics-stack mb-3">
      <thead><tr><th>Product</th><th>Counted</th></tr></thead>
      <tbody>{products.flatMap(product => placesOf(product).map(place => <tr key={`${product.id}-${entryKey(place)}`}>
        <td>{placedName(productName(product), place)}</td>
        <td data-label="Counted">
          <EntryFields product={product} place={place} entries={entries[product.id]} error={errors[product.id]?.[entryKey(place)]}
            idPrefix={`logistics-stock-initial-${product.id}-${entryKey(place)}`} prefix={productName(product)} compact
            onChange={next => setEntries({ ...entries, [product.id]: next })} />
        </td>
      </tr>))}</tbody>
    </Table>
    <FormGroup className="mb-0">
      <Label for="logistics-stock-initial-reason">Notes</Label>
      <Input id="logistics-stock-initial-reason" value={reason} maxLength={TEXT_LIMIT} placeholder="optional, e.g. Launch count, Câmpina"
        onChange={event => setReason(event.target.value)} />
    </FormGroup>
  </StockModal>
}

// A receipt, a manual issue or an inventory adjustment reversed, with a reason, by the inverse movement.
export function ReverseDialog({ movement, product, pending, failure, onSubmit, onClose }) {
  const [reason, setReason] = useState("")
  const [error, setError] = useState("")
  const unit = product?.unit
  const amount = quantity => `${quantity < 0 ? "−" : "+"}${quantityLabel(Math.abs(quantity), unit)}`
  const save = () => {
    if (!reason.trim()) return setError("A reason is required.")
    setError("")
    return onSubmit("reverseMovement", { movementId: movement.id, reason: reason.trim() })
  }
  return <StockModal title="Reverse movement" id="logistics-stock-reverse" pending={pending} failure={failure} onClose={onClose}
    actions={<Button color="danger" disabled={pending} onClick={save}>{pending ? "Reversing…" : "Reverse"}</Button>}>
    <p>Reverse the {reversedName(movement.type)} of <strong>{amount(movement.quantity)}</strong> of {placedName(movement.product.name, movement.place)}
      {" "}recorded on {timeLabel(movement.createdAt)}?</p>
    <p className="text-muted">An inverse movement of <strong>{amount(-movement.quantity)}</strong> is recorded; the original stays in the ledger, marked
      Reversed.</p>
    <FormGroup className="mb-0">
      <Label for="logistics-stock-reverse-reason">Reason</Label>
      <Input id="logistics-stock-reverse-reason" value={reason} maxLength={TEXT_LIMIT} invalid={Boolean(error)} onChange={event => setReason(event.target.value)} />
      {error && <FormFeedback>{error}</FormFeedback>}
    </FormGroup>
  </StockModal>
}
